import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { Field, change } from 'redux-form';
import Modal from 'react-modal';
import {
  isRequired, maxChar125, isValidTextAreaEntry, isWeekend, validatePickUpTime, hoursValidate, minutesValidate,
  closingTimeValidate,
  readyTimeNotBeforeNow,
} from '../../../utils/validator';
import {
  UploadDocumentsSubHead,
  BlockTitle,
  FormSummary,
  PageDescription,
  InputContainer,
  OptionsContainer,
  DateContainer,
  TimeContainer,
  TimeInputsContainer,
  TimeDivider,
  TimeWrapper,
  AdditionalNotesContainer,
  TimeError,
  ErrorMessage,
  ModalBody,
  ModalTitle,
  ButtonDiv,
} from '../css';
import UploadDocuments from '../../ReactComponent/UploadDocuments';
import ModalBoxStyle from '../../_styledComponents/ModalBoxStyle';
import SecondaryButton from '../../../lib/ui/buttons/SecondaryButton';
import { AMPM, IsYesOrNo } from '../../../utils/enums';
import DatePicker from '../../../lib/ui/form_elements/DatePicker';
import Dropdown from '../../../lib/ui/form_elements/Dropdown';
import ToggleButton from '../../../lib/ui/buttons/ToggleButton';
import TextArea from '../../../lib/ui/form_elements/TextArea';
import RenderField from '../../ReactComponent/RenderField';
import TermsAndConditions from './TermsAndConditions';

const Step4 = ({
  // eslint-disable-next-line no-unused-vars
  step, formData = {}, parentFields, formSyncErrors, dispatch, formName,
}) => {
  const today = useMemo(() => new Date(), []);
  const [fileErrorModal, setFileErrorModal] = useState(false);
  const handleFileError = useCallback(() => setFileErrorModal(true), []);

  const documentsInputs = [
    { id: 'documentsUpload', show: true },
  ];

  useEffect(() => {
    if (formData.hasDroppingFreightOff !== undefined) {
      const droppingFreight = (formData.hasDroppingFreightOff) ? IsYesOrNo.Yes : IsYesOrNo.No;
      const droppingFreightObj = {
        name: droppingFreight,
        value: droppingFreight === IsYesOrNo.Yes,
      };
      dispatch(change(formName, 'droppingFreightOff', droppingFreightObj));
    }
  }, [formData.hasDroppingFreightOff]);

  useEffect(() => {
    if (step === 3 && formData.droppingFreightOff && formData.droppingFreightOff.value) {
      dispatch(change(formName, 'regularScheduledPickup', { name: IsYesOrNo.No, value: false }));
    }
  }, [step, formData.droppingFreightOff?.value]);

  const documentsToShow = documentsInputs.filter((d) => d.show === true);
  if (step === 3) {
    return (
      <>
        <FormSummary>

          <PageDescription>{t('PickUpDetailsInfo')}</PageDescription>
          <fieldset>
            <InputContainer>
              <BlockTitle>
                {t('DroppingFreightOffTitle')}
              </BlockTitle>
              <OptionsContainer>
                <Field
                  name="droppingFreightOff"
                  component={RenderField}
                  type="radio"
                  value={formData.droppingFreightOff}
                  data={[
                    { name: IsYesOrNo.Yes, value: true },
                    { name: IsYesOrNo.No, value: false },
                  ]}
                />
              </OptionsContainer>
            </InputContainer>
            {formData.droppingFreightOff.value && <ErrorMessage>{t('DroppingFreightOffMessage')}</ErrorMessage>}
          </fieldset>
          <fieldset>
            <InputContainer>
              <BlockTitle>
                {t('RegularScheduledPickupTitle')}
              </BlockTitle>
              <OptionsContainer>
                <Field
                  name="regularScheduledPickup"
                  component={RenderField}
                  type="radio"
                  value={formData.regularScheduledPickup}
                  disabled={formData.droppingFreightOff.value}
                  data={[
                    { name: IsYesOrNo.Yes, value: true },
                    { name: IsYesOrNo.No, value: false },
                  ]}
                />
              </OptionsContainer>
            </InputContainer>
            {formData.regularScheduledPickup.value && <ErrorMessage>{t('RegularScheduledPickupMessage')}</ErrorMessage>}
          </fieldset>
          <fieldset>
            <InputContainer>
              <BlockTitle>
                {t('EnterPickupDate')}
              </BlockTitle>
              <DateContainer>
                <Field
                  name="pickupDate"
                  component={DatePicker}
                  label={t('Date')}
                  initialDate={today}
                  validate={[isRequired, isWeekend]}
                />
              </DateContainer>

            </InputContainer>
          </fieldset>

          <TimeWrapper>
            <fieldset>
              <InputContainer>
                <BlockTitle>
                  {t('ReadyTime')}
                  :
                </BlockTitle>
                <TimeContainer>
                  <TimeInputsContainer>
                    <Field
                      name="readyTime.hours"
                      label={t('Hour')}
                      page={t('CreateShipment')}
                      component={Dropdown}
                      formData={formData}
                      validate={[validatePickUpTime, hoursValidate, isRequired, readyTimeNotBeforeNow]}
                      required
                      options={[
                        { value: '' },
                        ...Array(12).keys()].map((item) => (typeof item === 'object' ? item : { value: item + 1 }))}
                    />
                    <TimeDivider>:</TimeDivider>
                    <Field
                      name="readyTime.minutes"
                      label={t('Minutes')}
                      component={Dropdown}
                      page={t('CreateShipment')}
                      formData={formData}
                      validate={[validatePickUpTime, minutesValidate, isRequired, readyTimeNotBeforeNow]}
                      required
                      options={['', '00', '15', '30', '45'].map((item) => ({ value: item }))}
                    />
                  </TimeInputsContainer>
                  <Field
                    name="readyTime.amOrPm"
                    component={ToggleButton}
                    validate={[validatePickUpTime, isRequired, readyTimeNotBeforeNow]}
                    label1={AMPM.AM}
                    required
                    label2={AMPM.PM}
                  />
                </TimeContainer>
              </InputContainer>
              {formSyncErrors.readyTime
                && (
                <TimeError>
                  {formSyncErrors.readyTime?.hours || formSyncErrors.readyTime?.minutes}
                </TimeError>
                )}
            </fieldset>
            <fieldset>
              <InputContainer>
                <BlockTitle>
                  {t('ClosingTime')}
                  :
                </BlockTitle>
                <TimeContainer>
                  <TimeInputsContainer>
                    <Field
                      name="closingTime.hours"
                      label={t('Hour')}
                      component={Dropdown}
                      validate={[validatePickUpTime, hoursValidate, closingTimeValidate]}
                      options={[
                        { value: '' },
                        ...Array(12).keys()].map((item) => (typeof item === 'object' ? item : { value: item + 1 }))}
                    />
                    <TimeDivider>:</TimeDivider>
                    <Field
                      name="closingTime.minutes"
                      label={t('Minutes')}
                      component={Dropdown}
                      validate={[validatePickUpTime, minutesValidate]}
                      options={['', '00', '15', '30', '45'].map((item) => ({ value: item }))}
                    />
                  </TimeInputsContainer>
                  <Field
                    name="closingTime.amOrPm"
                    component={ToggleButton}
                    label1={AMPM.AM}
                    label2={AMPM.PM}
                  />
                </TimeContainer>
              </InputContainer>
              {formSyncErrors.closingTime
                && (
                <TimeError>
                  {formSyncErrors.closingTime?.hours || formSyncErrors.closingTime?.minutes}
                </TimeError>
                )}
            </fieldset>
          </TimeWrapper>

          <div>
            <fieldset>
              <InputContainer>
                <BlockTitle>
                  {t('UploadDocuments')}
                  {' '}
                  <span>
                    (
                    {t('Optional')}
                    )
                  </span>
                </BlockTitle>
                <UploadDocumentsSubHead>
                  <p>
                    {t('maxFileSize')}
                    {' '}
                    4 MB
                  </p>
                  <br />
                  <br />
                  <p>
                    <span>
                      {t('Note')}
                      :
                      {' '}
                    </span>
                    {t('paperworkWarning')}
                  </p>
                </UploadDocumentsSubHead>
                <UploadDocuments
                  documentFields={documentsToShow}
                  attachedDocuments={formData && formData.attachedDocuments}
                  onFileError={handleFileError}
                />
              </InputContainer>
            </fieldset>
          </div>

          <InputContainer>
            <BlockTitle>
              {t('AdditionalNotes')}
              {' '}
              <span>
                (
                {t('Optional')}
                )
              </span>
            </BlockTitle>
            <AdditionalNotesContainer>
              <Field
                name="specialInstructions"
                component={TextArea}
                validate={[maxChar125, isValidTextAreaEntry]}
                placeholder={t('Notes')}
                maxLength={125}
              />
            </AdditionalNotesContainer>
          </InputContainer>

          <TermsAndConditions />
        </FormSummary>

        <Modal
          isOpen={fileErrorModal}
          onRequestClose={() => setFileErrorModal(false)}
          style={ModalBoxStyle}
          contentLabel="File Error Modal"
        >
          <ModalBody>
            <ModalTitle>{t('WarningTitle')}</ModalTitle>
            <p>{t('FileFormatPopupError')}</p>
            <ButtonDiv className="row no-gutters">
              <div className="col-sm-12">
                <SecondaryButton
                  name="acceptFileError"
                  label={t('Accept')}
                  onClick={() => setFileErrorModal(false)}
                />
              </div>
            </ButtonDiv>
          </ModalBody>
        </Modal>
      </>
    );
  }

  return null;
};

Step4.propTypes = {
  step: PropTypes.number,
  formData: PropTypes.shape({
    hasDroppingFreightOff: PropTypes.bool,
    droppingFreightOff: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.bool,
    }),
    regularScheduledPickup: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.bool,
    }),
    pickupDate: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.string,
    ]),
    readyTime: PropTypes.shape({
      hours: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      minutes: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      amOrPm: PropTypes.string,
    }),
    closingTime: PropTypes.shape({
      hours: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      minutes: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      amOrPm: PropTypes.string,
    }),
    attachedDocuments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    specialInstructions: PropTypes.string,
  }),
  parentFields: PropTypes.object,
  formSyncErrors: PropTypes.object,
  dispatch: PropTypes.func,
  formName: PropTypes.string,
};

export default Step4;
